import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Calendar, Receipt, Building2, Users, Percent } from "lucide-react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};
const firstOfMonth = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const lastOfMonth = (dateStr) => {
  const d = new Date(dateStr);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
};

export default function GstReport() {
  const [mode, setMode] = useState("month"); // month | range
  const [monthPick, setMonthPick] = useState(() => todayIST().slice(0, 7));
  const [dateFrom, setDateFrom] = useState(firstOfMonth(todayIST()));
  const [dateTo, setDateTo] = useState(todayIST());
  const [config, setConfig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Derived range
  const range = mode === "month"
    ? { date_from: `${monthPick}-01`, date_to: lastOfMonth(`${monthPick}-01`) }
    : { date_from: dateFrom, date_to: dateTo };

  useEffect(() => {
    api.get("/gst/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  async function loadPreview() {
    setLoading(true);
    try {
      const { data } = await api.get("/gst/preview", { params: range });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [monthPick, mode, dateFrom, dateTo]);

  async function download() {
    setDownloading(true);
    try {
      const res = await api.get("/gst/export.xlsx", { params: range, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `closet-gst-${range.date_from}-to-${range.date_to}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success("GST workbook downloaded — open in Excel.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setDownloading(false); }
  }

  // Month picker helper — build a compact grid of last 12 months
  const monthOptions = (() => {
    const now = new Date(todayIST());
    const out = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push({ key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    }
    return out;
  })();

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="gst-report-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ COMPLIANCE · GSTR-1 EXPORT</div>
          <h1 className="section-title text-5xl lg:text-6xl">GST Report.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">One-click multi-sheet Excel — Orders, Refunds, Tax Summary, B2B, B2C, HSN Summary. Auto-emailed to you every 1st of the month.</p>
        </div>
        <button onClick={download} disabled={downloading || !preview} className="brut-btn px-6 py-3 inline-flex items-center gap-2 self-start disabled:opacity-50" data-testid="download-gst-xlsx-btn">
          <Download size={16} /> {downloading ? "Building…" : "Download GST Excel"}
        </button>
      </header>

      {/* Config strip */}
      {config && (
        <div className="brut-card p-4 mb-5 flex flex-wrap gap-6 items-center" data-testid="gst-config-strip">
          <Cfg icon={Building2} label="GSTIN" value={config.gstin} />
          <Cfg icon={FileText} label="State" value={config.state} />
          <Cfg icon={Percent} label="Rate" value={`${config.default_rate}%`} />
          <Cfg icon={Receipt} label="HSN" value={config.default_hsn} />
          <Cfg icon={Users} label="Prices" value={config.prices_inclusive ? "GST-inclusive" : "GST-exclusive"} />
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex gap-2 mb-4" data-testid="gst-mode-tabs">
        <button onClick={() => setMode("month")} className={`px-5 py-2.5 border-2 border-black font-mono-ds text-xs uppercase tracking-widest font-bold ${mode === "month" ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white hover:bg-zinc-100"}`} data-testid="mode-month">
          <Calendar size={12} className="inline mr-1" /> Monthly
        </button>
        <button onClick={() => setMode("range")} className={`px-5 py-2.5 border-2 border-black font-mono-ds text-xs uppercase tracking-widest font-bold ${mode === "range" ? "bg-black text-white shadow-[3px_3px_0_0_#0a0a0a]" : "bg-white hover:bg-zinc-100"}`} data-testid="mode-range">
          Date range
        </button>
      </div>

      {/* Picker */}
      <div className="brut-card p-4 mb-6" data-testid="gst-picker">
        {mode === "month" ? (
          <div className="flex flex-wrap gap-2">
            {monthOptions.map((m) => (
              <button key={m.key} onClick={() => setMonthPick(m.key)}
                className={`px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider ${monthPick === m.key ? "bg-[#7A3E1F] text-white" : "bg-white hover:bg-zinc-100"}`}
                data-testid={`month-${m.key}`}>{m.label}</button>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 items-center">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">From</div>
            <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="gst-date-from" />
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">To</div>
            <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="gst-date-to" />
          </div>
        )}
        <div className="mt-3 font-mono-ds text-[11px] tracking-widest uppercase text-zinc-500">
          Range: <span className="text-zinc-900 font-bold">{range.date_from} → {range.date_to}</span>
        </div>
      </div>

      {/* Preview */}
      {loading ? (
        <div className="brut-card p-10 text-center font-mono-ds text-xs uppercase text-zinc-500">Loading preview…</div>
      ) : preview ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Stat testid="gst-stat-sales" label="Sales rows" value={preview.sales_count} />
            <Stat testid="gst-stat-refunds" label="Refund rows" value={preview.refund_count} />
            <Stat testid="gst-stat-b2b" label="B2B rows" value={preview.b2b_count} />
            <Stat testid="gst-stat-b2c" label="B2C rows" value={preview.b2c_count} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat testid="gst-stat-gross" label="Gross sales" value={`₹${preview.gross_sales.toLocaleString("en-IN")}`} />
            <Stat testid="gst-stat-taxable" label="Taxable value" value={`₹${preview.taxable_sales.toLocaleString("en-IN")}`} />
            <Stat testid="gst-stat-tax" label="Total GST" value={`₹${preview.total_tax.toLocaleString("en-IN")}`} accent />
            <Stat testid="gst-stat-refund-amt" label="Refunds"
              value={`₹${preview.refund_amount.toLocaleString("en-IN")}`}
              sub={`Tax reversed: ₹${Math.abs(preview.tax_reversed).toLocaleString("en-IN")}`} />
          </div>
          <div className="brut-card p-5 mb-6" data-testid="gst-tax-split">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Tax split</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="font-mono-ds text-[10px] uppercase text-zinc-500">CGST</div>
                <div className="font-display font-black text-2xl">₹{preview.total_cgst.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="font-mono-ds text-[10px] uppercase text-zinc-500">SGST</div>
                <div className="font-display font-black text-2xl">₹{preview.total_sgst.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="font-mono-ds text-[10px] uppercase text-zinc-500">IGST</div>
                <div className="font-display font-black text-2xl">₹{preview.total_igst.toLocaleString("en-IN")}</div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="brut-card p-10 text-center font-mono-ds text-xs uppercase text-zinc-500">No data for this range</div>
      )}

      <div className="brut-card-flat bg-[#FDF6EE] p-5 mt-4">
        <div className="font-display font-bold text-sm mb-2">What's inside the Excel:</div>
        <ul className="text-sm text-zinc-700 space-y-1">
          <li>· <b>Summary</b> — cover sheet with your GSTIN, range, and headline totals</li>
          <li>· <b>Orders</b> — every sale row with GST breakdown (CGST / SGST / IGST split)</li>
          <li>· <b>Refunds</b> — every refund with reversed tax</li>
          <li>· <b>Tax Summary</b> — grouped by GST rate (your GSTR-1 tax-rate table)</li>
          <li>· <b>B2B</b> — sales to customers with GSTIN (populate customer GSTIN on the order to see rows here)</li>
          <li>· <b>B2C</b> — sales without GSTIN</li>
          <li>· <b>HSN Summary</b> — GSTR-1 HSN-wise breakup (HSN + rate + qty + taxable + tax)</li>
        </ul>
      </div>
    </div>
  );
}

function Cfg({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-zinc-500" />}
      <div>
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{label}</div>
        <div className="font-display font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className={`brut-card p-4 ${accent ? "!bg-black" : "bg-white"}`} data-testid={testid}>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase ${accent ? "text-white/70" : "text-zinc-500"}`}>{label}</div>
      <div className={`font-display font-black text-2xl mt-1 ${accent ? "text-white" : ""}`}>{value}</div>
      {sub && <div className={`font-mono-ds text-[10px] mt-1 ${accent ? "text-white/70" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}
